import * as Sentry from '@sentry/nextjs';
import type { ReactElement, ReactNode } from 'react';
import { cloneElement, isValidElement, useEffect, useState } from 'react';
import {
  FloatingFocusManager,
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  safePolygon,
  shift,
  type Placement,
  useClick,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
} from '@floating-ui/react';

import { getFloatingPortalRoot, toggleTrustBadge } from '@tmh-website/services/floating-ui.service';
import { type DropdownThemeColor, StyledDropdown } from '@tmh-website/ui/Dropdown';
import { useIsMobile } from '@tmh-website/shared';

import * as Styled from './DropdownHover.styles';

export interface DropdownHoverProps {
  /**
   * Anchor element to toggle this dropdown.
   */
  anchorEl: ReactElement;
  defaultPlacement?: Placement;
  children?: ReactNode;
  themeColor: DropdownThemeColor;
  /**
   * Force closing the dropdown by setting this to true (e.g. when hovering over a different dropdown)
   */
  forceClose?: boolean;
  /**
   * A callback that runs when opening or closing the dropdown.
   */
  onOpenChange?: (open: boolean) => void;
}

const DropdownHover = ({
  children,
  anchorEl,
  defaultPlacement = 'bottom-start',
  themeColor,
  forceClose,
  onOpenChange,
}: DropdownHoverProps) => {
  const [open, setOpen] = useState(false);
  const [isInDOM, setIsInDOM] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const isMobile = useIsMobile();

  /**
   * Force close dropdown
   */
  useEffect(() => {
    if (forceClose) {
      setOpen(false);
    }
  }, [forceClose]);

  const { x, y, reference, floating, strategy, context, placement } = useFloating<HTMLButtonElement>({
    open,
    onOpenChange: setOpen,
    middleware: [offset({ mainAxis: 4 }), flip(), shift()],
    placement: defaultPlacement,
    whileElementsMounted: autoUpdate,
  });

  // Set transform origin for css transition
  let transformOriginX, transformOriginY;
  if (placement.includes('start')) {
    transformOriginX = 'left';
  } else if (placement.includes('end')) {
    transformOriginX = 'right';
  } else {
    transformOriginX = 'center';
  }
  if (placement.includes('top')) {
    transformOriginY = 'bottom';
  } else if (placement.includes('bottom')) {
    transformOriginY = 'top';
  } else {
    transformOriginY = 'center';
  }

  /**
   * Toggle dropdown with a delay to enable css transitions and hide trustbadge on mobile
   */
  useEffect(() => {
    if (isMobile) {
      toggleTrustBadge(!open);
    }

    let timeout: number;
    if (open) {
      timeout = window.setTimeout(() => {
        setIsInDOM(true);
        setIsVisible(true);
      }, 10);
    } else {
      setIsVisible(false);
      timeout = window.setTimeout(() => setIsInDOM(false), 300);
    }

    if (onOpenChange) {
      onOpenChange(open);
    }

    return () => {
      window.clearTimeout(timeout);
    };
  }, [open]);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useHover(context, { handleClose: safePolygon() }),
    useClick(context, { toggle: true, ignoreMouse: true }),
    useDismiss(context),
  ]);

  if (!isValidElement(anchorEl)) {
    const error = new Error('Invalid anchor element provided.');
    Sentry.captureException(error);
    throw error;
  }

  return (
    <>
      {cloneElement(anchorEl, { ...getReferenceProps({ ref: reference, open }) })}
      <FloatingPortal root={getFloatingPortalRoot()}>
        {(open || isInDOM) && (
          <FloatingFocusManager context={context} modal={false} order={['content']} initialFocus={-1}>
            <>
              <Styled.Container
                $isVisible={isVisible}
                $themeColor={themeColor}
                {...getFloatingProps({
                  ref: floating,
                  style: {
                    position: strategy,
                    top: y ?? 0,
                    left: x ?? 0,
                    transformOrigin: `${transformOriginX} ${transformOriginY}`,
                  },
                })}>
                {children}
              </Styled.Container>
              <StyledDropdown.BackgroundFade $isVisible={isVisible} />
            </>
          </FloatingFocusManager>
        )}
      </FloatingPortal>
    </>
  );
};

export default DropdownHover;
